export type SqliteKvSlot = {
  dbKey: string
  key: string
}

export type SqliteKvMemory =
  | { state: 'absent' }
  | { state: 'present', value: string }

type OpenDb = {
  state: 'open'
  cells: Map<string, SqliteKvMemory>
}

type DbState = OpenDb | { state: 'forgotten' }

const memory = new Map<string, DbState>()

export function sqliteKvSlot (dbKey: string, key: string): SqliteKvSlot {
  return { dbKey, key }
}

function openDb (dbKey: string): OpenDb | undefined {
  const db = memory.get(dbKey)
  if (db?.state !== 'open') {
    return undefined
  }
  return db
}

function cellsFor (dbKey: string): OpenDb | undefined {
  const existing = memory.get(dbKey)
  if (existing?.state === 'forgotten') {
    return undefined
  }
  if (existing?.state === 'open') {
    return existing
  }
  const opened: OpenDb = { state: 'open', cells: new Map() }
  memory.set(dbKey, opened)
  return opened
}

function peek (slot: SqliteKvSlot): SqliteKvMemory | undefined {
  return openDb(slot.dbKey)?.cells.get(slot.key)
}

export async function readSqliteKv (
  slot: SqliteKvSlot,
  select: () => Promise<string | undefined>,
): Promise<string | undefined> {
  const known = peek(slot)
  if (known?.state === 'present') {
    return known.value
  }
  if (known?.state === 'absent') {
    return undefined
  }

  const opened = cellsFor(slot.dbKey)
  if (!opened) {
    return select()
  }
  const raw = await select()
  if (memory.get(slot.dbKey) !== opened) {
    return raw
  }
  const written = peek(slot)
  if (written) {
    return written.state === 'present' ? written.value : undefined
  }
  if (raw === undefined) {
    opened.cells.set(slot.key, { state: 'absent' })
  } else {
    opened.cells.set(slot.key, { state: 'present', value: raw })
  }
  return raw
}

export function rememberSqliteKvWrite (slot: SqliteKvSlot, value: string): void {
  const db = openDb(slot.dbKey)
  if (!db) {
    return
  }
  db.cells.set(slot.key, { state: 'present', value })
}

export function forgetSqliteKv (dbKey: string): void {
  memory.set(dbKey, { state: 'forgotten' })
}

export function resetSqliteKv (dbKey: string): void {
  memory.delete(dbKey)
}
